import React, { memo } from 'react';

import {
  Breakpoints,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import { Content } from './Content';
import {
  MaintenanceSchedulePackagesData,
  TimeBasedServices,
} from '../../typings/general';
import {
  StyledContainer,
  StyledMainContainer,
  StyledMileageContainer,
  StyledWrapper,
} from './styled';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';

interface InformationCardProps {
  additionalServices: TimeBasedServices[];
  interval: string;
  isLastMaintenance: boolean | undefined;
  label: string;
  onOpenContentLayer: (value: boolean) => void;
  openCompleteScheduleView: boolean;
  packages?: MaintenanceSchedulePackagesData;
}

export const InformationCard = memo(
  ({
    interval,
    label,
    packages,
    openCompleteScheduleView,
    additionalServices,
    onOpenContentLayer,
    isLastMaintenance = false,
  }: InformationCardProps) => {
    const [store] = useStore();
    const { form } = store;
    const { translationHandler } = useMaintenanceContext();

    const isMileageInputed: boolean = !!form.mileage;
    const formattedInterval: string = Number(interval).toLocaleString();

    return (
      <StyledWrapper data-testid="information-card">
        <StyledMainContainer isMileageInputed={isMileageInputed}>
          <StyledContainer>
            <StyledMileageContainer>
              <TextCustom
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                    color: TextColor.tertiary,
                  },
                  [Breakpoints.b1920]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight32,
                  },
                }}
              >
                {label}
              </TextCustom>
              <TextCustom
                appearance={TextAppearance.headline300}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize28,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight32,
                  },
                  [Breakpoints.b1920]: {
                    fontSize: TextSize.TextSize32,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight36,
                  },
                }}
              >
                {formattedInterval} {translationHandler('maintenance.miles')}
              </TextCustom>
            </StyledMileageContainer>
            <Content
              packages={packages}
              onViewAllServices={onOpenContentLayer}
              viewCompleteSchedule={
                !isMileageInputed
                  ? openCompleteScheduleView
                  : !openCompleteScheduleView
              }
              additionalServices={additionalServices}
              isLastMaintenance={isLastMaintenance}
              showTitle={
                !isMileageInputed
                  ? openCompleteScheduleView
                  : !openCompleteScheduleView
              }
              nextMileage={interval}
            />
          </StyledContainer>
        </StyledMainContainer>
      </StyledWrapper>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.interval === nextProps.interval &&
      prevProps.label === nextProps.label &&
      prevProps.packages === nextProps.packages &&
      prevProps.openCompleteScheduleView ===
        nextProps.openCompleteScheduleView &&
      prevProps.additionalServices === nextProps.additionalServices &&
      prevProps.onOpenContentLayer === nextProps.onOpenContentLayer
    );
  },
);
